import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker immediately when this module is imported
// Use local worker file served from public directory for reliability
// Worker file is copied during postinstall from pdfjs-dist@5.4.149
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Information about a PDF document's protection and metadata
 */
export interface PdfInfo {
  /**
   * Whether the PDF is encrypted or password-protected
   */
  isEncrypted: boolean;
  /**
   * Whether a password is needed to open the document
   */
  isPasswordProtected: boolean;
  /** Number of pages in the document */
  numPages: number;
  /**
   * Permission information
   * - For unencrypted PDFs: Contains permission info (all true if unrestricted)
   * - For password-protected PDFs: null if we can't read without password
   */
  permissions: {
    /** Raw permission flags as a number */
    flags: number | null;
    /** Individual permission flags as boolean attributes */
    canPrint: boolean;
    canPrintHighQuality: boolean;
    canModify: boolean;
    canCopy: boolean;
    canAnnotate: boolean;
    canFillForms: boolean;
    canExtractForAccessibility: boolean;
    canAssemble: boolean;
    /** Human-readable descriptions of what's allowed */
    descriptions: string[];
  } | null;
  /** Document metadata */
  metadata: {
    title?: string;
    author?: string;
    subject?: string;
    creator?: string;
    producer?: string;
    keywords?: string[];
    creationDate?: Date;
    modificationDate?: Date;
  };
}

/**
 * PDF permission bit flags (from PDF specification)
 * These are the bit positions in the /P (permissions) entry
 */
const PDF_PERMISSIONS = {
  PRINT: 1 << 2, // Bit 3 (position 2): Print
  MODIFY: 1 << 3, // Bit 4 (position 3): Modify contents
  COPY: 1 << 4, // Bit 5 (position 4): Copy/extract text and graphics
  ANNOTATIONS: 1 << 5, // Bit 6 (position 5): Add/modify text annotations, fill forms
  FILL_FORMS: 1 << 8, // Bit 9 (position 8): Fill in existing form fields
  EXTRACT: 1 << 9, // Bit 10 (position 9): Extract text/graphics for accessibility
  ASSEMBLE: 1 << 10, // Bit 11 (position 10): Assemble document (insert, rotate, delete pages)
  PRINT_HIGH: 1 << 11, // Bit 12 (position 11): Print high quality
} as const;

/**
 * Decode permission flags into human-readable descriptions
 */
function decodePermissions(flags: number): string[] {
  const descriptions: string[] = [];

  if (flags & PDF_PERMISSIONS.PRINT) {
    descriptions.push('Print document');
  }
  if (flags & PDF_PERMISSIONS.PRINT_HIGH) {
    descriptions.push('Print high quality');
  }
  if (flags & PDF_PERMISSIONS.MODIFY) {
    descriptions.push('Modify contents');
  }
  if (flags & PDF_PERMISSIONS.COPY) {
    descriptions.push('Copy/extract text and graphics');
  }
  if (flags & PDF_PERMISSIONS.ANNOTATIONS) {
    descriptions.push('Add/modify annotations and form fields');
  }
  if (flags & PDF_PERMISSIONS.FILL_FORMS) {
    descriptions.push('Fill in form fields');
  }
  if (flags & PDF_PERMISSIONS.EXTRACT) {
    descriptions.push('Extract text for accessibility');
  }
  if (flags & PDF_PERMISSIONS.ASSEMBLE) {
    descriptions.push('Assemble document (insert, rotate, delete pages)');
  }

  return descriptions;
}

/**
 * Parse date string from PDF metadata
 */
function parsePdfDate(dateStr?: string): Date | undefined {
  if (!dateStr) return undefined;

  // PDF date format: D:YYYYMMDDHHmmSSOHH'mm'
  const match = dateStr.match(/^D:(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    return new Date(
      parseInt(year!),
      parseInt(month!) - 1,
      parseInt(day!),
      parseInt(hour!),
      parseInt(minute!),
      parseInt(second!)
    );
  }

  // Try standard Date parsing as fallback
  try {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? undefined : date;
  } catch {
    return undefined;
  }
}

/**
 * Check PDF protection status and extract comprehensive information
 *
 * @param input - PDF document as Blob, File, ArrayBuffer, or Uint8Array
 * @returns Comprehensive information about the PDF document
 *
 * @example
 * ```typescript
 * // From a File input
 * const file = event.target.files[0];
 * const info = await checkPdfProtection(file);
 * console.log('Encrypted:', info.isEncrypted);
 * console.log('Can print:', info.permissions?.canPrint);
 * console.log('Can modify:', info.permissions?.canModify);
 *
 * // From a Blob
 * const blob = new Blob([pdfData], { type: 'application/pdf' });
 * const info = await checkPdfProtection(blob);
 *
 * // From an ArrayBuffer
 * const arrayBuffer = await fetch('document.pdf').then(r => r.arrayBuffer());
 * const info = await checkPdfProtection(arrayBuffer);
 * ```
 */
export async function checkPdfProtection(
  input: Blob | File | ArrayBuffer | Uint8Array
): Promise<PdfInfo> {
  console.log('[checkPdfProtection] Starting PDF check');
  console.log('[checkPdfProtection] PDF.js API version:', pdfjsLib.version);
  console.log('[checkPdfProtection] Worker source:', pdfjsLib.GlobalWorkerOptions.workerSrc);

  // Convert input to Uint8Array
  let pdfData: Uint8Array;

  if (input instanceof Blob || input instanceof File) {
    const arrayBuffer = await input.arrayBuffer();
    pdfData = new Uint8Array(arrayBuffer);
  } else if (input instanceof ArrayBuffer) {
    pdfData = new Uint8Array(input);
  } else {
    pdfData = input;
  }

  console.log('[checkPdfProtection] PDF data size:', pdfData.length, 'bytes');

  // Load the PDF document
  const loadingTask = pdfjsLib.getDocument({ data: pdfData });

  let isPasswordProtected = false;
  let pdfDocument;

  try {
    pdfDocument = await loadingTask.promise;
  } catch (error: any) {
    // Check if it's a password error
    if (error.name === 'PasswordException') {
      isPasswordProtected = true;
      // Try to load with empty password to get basic info
      const retryTask = pdfjsLib.getDocument({ data: pdfData, password: '' });
      try {
        pdfDocument = await retryTask.promise;
      } catch {
        // If still fails, return minimal info
        return {
          isEncrypted: true,
          isPasswordProtected: true,
          numPages: 0,
          permissions: null,
          metadata: {},
        };
      }
    } else {
      throw error;
    }
  }

  // Get document metadata
  const metadataResult = await pdfDocument.getMetadata();
  const info = metadataResult.info as Record<string, any> | null;

  const metadata = {
    title: info?.Title || undefined,
    author: info?.Author || undefined,
    subject: info?.Subject || undefined,
    creator: info?.Creator || undefined,
    producer: info?.Producer || undefined,
    keywords: info?.Keywords?.split(/[,;]/)
      .map((k: string) => k.trim())
      .filter(Boolean),
    creationDate: parsePdfDate(info?.CreationDate),
    modificationDate: parsePdfDate(info?.ModDate),
  };

  // Get permissions
  const permissionsArray = await pdfDocument.getPermissions();
  const numPages = pdfDocument.numPages;

  let permissions;

  if (permissionsArray === null) {
    // No encryption or restrictions - fully unrestricted
    permissions = {
      flags: null,
      canPrint: true,
      canPrintHighQuality: true,
      canModify: true,
      canCopy: true,
      canAnnotate: true,
      canFillForms: true,
      canExtractForAccessibility: true,
      canAssemble: true,
      descriptions: [
        'Print document',
        'Print high quality',
        'Modify contents',
        'Copy/extract text and graphics',
        'Add/modify annotations and form fields',
        'Fill in form fields',
        'Extract text for accessibility',
        'Assemble document (insert, rotate, delete pages)',
      ],
    };
  } else if (Array.isArray(permissionsArray) && permissionsArray.length > 0) {
    // pdfjs returns permission flags as a number
    const flags = permissionsArray[0] as number;

    permissions = {
      flags,
      canPrint: !!(flags & PDF_PERMISSIONS.PRINT),
      canPrintHighQuality: !!(flags & PDF_PERMISSIONS.PRINT_HIGH),
      canModify: !!(flags & PDF_PERMISSIONS.MODIFY),
      canCopy: !!(flags & PDF_PERMISSIONS.COPY),
      canAnnotate: !!(flags & PDF_PERMISSIONS.ANNOTATIONS),
      canFillForms: !!(flags & PDF_PERMISSIONS.FILL_FORMS),
      canExtractForAccessibility: !!(flags & PDF_PERMISSIONS.EXTRACT),
      canAssemble: !!(flags & PDF_PERMISSIONS.ASSEMBLE),
      descriptions: decodePermissions(flags),
    };
  } else {
    // Encrypted but we can't read permissions
    permissions = null;
  }

  return {
    isEncrypted: permissionsArray !== null || isPasswordProtected,
    isPasswordProtected,
    numPages,
    permissions,
    metadata,
  };
}

/**
 * Check if a PDF is signable (meets ActaSign requirements)
 *
 * Requirements:
 * - Must NOT be encrypted or password-protected
 * - Must have canAnnotate permission
 * - Must have canFillForms permission
 *
 * @param pdfInfo - PDF information from checkPdfProtection
 * @returns Object with isSignable flag and reason if not signable
 */
export function isPdfSignable(pdfInfo: PdfInfo): { isSignable: boolean; reason?: string } {
  // Block all encrypted/protected PDFs
  if (pdfInfo.isEncrypted || pdfInfo.isPasswordProtected) {
    return {
      isSignable: false,
      reason: 'pdfVerification.encrypted',
    };
  }

  // Check required permissions
  if (!pdfInfo.permissions) {
    return {
      isSignable: false,
      reason: 'pdfVerification.noPermissions',
    };
  }

  // Must have both canAnnotate AND canFillForms
  if (!pdfInfo.permissions.canAnnotate) {
    return {
      isSignable: false,
      reason: 'pdfVerification.noAnnotatePermission',
    };
  }

  if (!pdfInfo.permissions.canFillForms) {
    return {
      isSignable: false,
      reason: 'pdfVerification.noFillFormsPermission',
    };
  }

  return { isSignable: true };
}
